/**
 * QUIPWA — AuthProvider
 *
 * React view of the `authSession` state machine (lib/auth). Components read
 * the status and call the transitions; they never touch tokens.
 */
import { useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { authSession } from '../lib/auth/index.ts'
import { AuthContext, type AuthContextValue } from './AuthContext.ts'

export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(
    (onChange) => authSession.subscribe(onChange),
    () => authSession.state
  )

  const value = useMemo<AuthContextValue>(() => ({
    user: state.user,
    status: state.status,
    isAuthenticated: state.status === 'active',
    hasAccount: authSession.ownerEmail !== null,
    isLoading: false,
    pendingUser: state.pendingUser,
    login: () => authSession.signIn(window.location.hash.replace(/^#/, '') || '/'),
    reconnect: () => authSession.reconnect(window.location.hash.replace(/^#/, '') || '/'),
    grantPermissions: (extra = []) => authSession.grantPermissions(extra, window.location.hash.replace(/^#/, '') || '/'),
    logout: () => authSession.signOut(),
  }), [state])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
